/*
 * home-table-datasource.ts
 * dev-utms-shop-admin
 * 
 * Last Modified: 02.07.2025
 * Modified By: Tetiana Makovii
 * 
 * Created by Tetiana Makovii on 04.07.2023
 * Copyright © 2026 Sonova AG. All rights reserved.
 */



import { DataSource } from '@angular/cdk/collections';
import { Observable, BehaviorSubject, Subscription, first, skip } from 'rxjs';
import { SortingHeadersEnum } from './enums/sorting-headers-enum';
import { SortingKeysEnum } from './enums/sorting-keys-enum';
import { ClinicOfficeListItem } from 'src/shop-manager/common/models/internal/clinic-office-list-item.model';
import { ClinicOfficeListService } from 'src/shop-manager/services/clinic-office-list.service';
import { ShopStateEnum } from 'src/shop-manager/common/enums/shop-state-enum';
import { SortingDirectionsEnum } from 'src/common-panel/enums/sorting-directions-enum';
import { ShopTypesEnum } from 'src/common-panel/enums/shop-types-enum';
import { CountryId } from 'src/common-panel/constants/country-configs.constants';



export class HomeTableDataSource extends DataSource<ClinicOfficeListItem>
{
    subscription: Subscription = new Subscription()

    private _data: ClinicOfficeListItem[] = []
    private _sortDirection: SortingDirectionsEnum
    private _sortingHeader: SortingHeadersEnum

    private _countryFilterState

    private _pageSize: number
    private _pageIndex: number

    private clinicsOfficesSubject = new BehaviorSubject<ClinicOfficeListItem[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false)

    public loading$ = this.loadingSubject.asObservable()

    get count()
    {
        return this._data.length
    }

    get data()
    {
        return this._data
    }

    set sortDirection(value: SortingDirectionsEnum)
    {
        this._sortDirection = value
        this.clinicOfficeListService.tableSettings.sortDirection = value
    }

    get sortDirection(): SortingDirectionsEnum
    {
        return this._sortDirection
    }

    set sortingHeader(value: SortingHeadersEnum)
    {
        this._sortingHeader = value
        this.clinicOfficeListService.tableSettings.sortingHeader = value
    }

    get sortingHeader(): SortingHeadersEnum
    {
        return this._sortingHeader
    }

    set countryFilter(state: CountryId | undefined)
    {
        this._countryFilterState = state
        this.clinicOfficeListService.tableSettings.countryFilterState = state
    }

    get countryFilter(): CountryId | undefined
    {
        return this._countryFilterState
    }


    set pageSize(value: number)
    {
        this._pageSize = value
        this.clinicOfficeListService.tableSettings.pageSize = value
    }

    get pageSize(): number
    {
        return this._pageSize
    }

    set pageIndex(value: number)
    {
        this._pageIndex = value
        this.clinicOfficeListService.tableSettings.pageIndex = value
    }

    get pageIndex(): number
    {
        return this._pageIndex
    }

    constructor(private clinicOfficeListService: ClinicOfficeListService)
    {
        super();
        this._sortDirection = this.clinicOfficeListService.tableSettings.sortDirection;
        this._sortingHeader = this.clinicOfficeListService.tableSettings.sortingHeader;
        this._pageSize = this.clinicOfficeListService.tableSettings.pageSize;
        this._pageIndex = this.clinicOfficeListService.tableSettings.pageIndex;

        this._countryFilterState = this.clinicOfficeListService.tableSettings.countryFilterState;
    }


    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */

    connect(): Observable<ClinicOfficeListItem[]>
    {
        return this.clinicsOfficesSubject.asObservable();
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */

    disconnect()
    {
        this.clinicsOfficesSubject.complete();
        this.loadingSubject.complete();
        this.subscription.unsubscribe()
    }


    loadClinicsOffices()
    {
        this.clinicsOfficesSubject.next([])
        if (this.clinicOfficeListService.filterByCountry.value)
        {
            this.loadingSubject.next(true)


            if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();

            if (this.clinicOfficeListService.filterByShopType.value == ShopTypesEnum.clinic)
            {
                if (this.clinicOfficeListService.filterByShopState.value == ShopStateEnum.published)
                {
                    console.log("load")
                    let l_sub = this.clinicOfficeListService.loadClinicsData(this.clinicOfficeListService.filterByCountry.value).pipe(first(val => val == true)).subscribe(() =>
                    {
                        this._data = this.clinicOfficeListService.data.slice()
                        if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();

                        this.loadingSubject.next(false)
                        if (this.clinicOfficeListService.filterByIdCityOrName.value.length > 0)
                        {
                            this._data = this._data.filter(l_shopClinic => l_shopClinic.city?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.clinicOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.entOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.name?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase()))

                        }
                        this.loadClinicsOfficesPage()
                    })
                    this.subscription.add(l_sub)
                } else if (this.clinicOfficeListService.filterByShopState.value == ShopStateEnum.requested)
                {
                    let l_sub = this.clinicOfficeListService.loadRequestedClinicsData(this.clinicOfficeListService.filterByCountry.value).pipe(first(val => val == true)).subscribe(() =>
                    {
                        this._data = this.clinicOfficeListService.data.slice()
                        if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();

                        this.loadingSubject.next(false)
                        if (this.clinicOfficeListService.filterByIdCityOrName.value.length > 0)
                        {
                            this._data = this._data.filter(l_shopClinic => l_shopClinic.city?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.clinicOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.entOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.name?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase()))

                        }
                        this.pageIndex = 0;
                        this.loadClinicsOfficesPage()
                    })
                    this.subscription.add(l_sub)
                }
            } else if (this.clinicOfficeListService.filterByShopType.value == ShopTypesEnum.entOffice)
            {
                if (this.clinicOfficeListService.filterByShopState.value == ShopStateEnum.published)
                {
                    let l_sub = this.clinicOfficeListService.loadOfficesData(this.clinicOfficeListService.filterByCountry.value).pipe(first(val => val == true)).subscribe(() =>
                    {
                        this._data = this.clinicOfficeListService.data.slice()
                        if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();

                        this.loadingSubject.next(false)
                        if (this.clinicOfficeListService.filterByIdCityOrName.value.length > 0)
                        {
                            this._data = this._data.filter(l_shopClinic => l_shopClinic.city?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.clinicOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.entOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.name?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase()))

                        }
                        this.pageIndex = 0;
                        this.loadClinicsOfficesPage()

                    })
                    this.subscription.add(l_sub)
                } else if (this.clinicOfficeListService.filterByShopState.value == ShopStateEnum.requested)
                {
                    let l_sub = this.clinicOfficeListService.loadRequestedOfficesData(this.clinicOfficeListService.filterByCountry.value).pipe(first(val => val == true)).subscribe(() =>
                    {
                        this._data = this.clinicOfficeListService.data.slice()
                        if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();

                        this.loadingSubject.next(false)
                        if (this.clinicOfficeListService.filterByIdCityOrName.value.length > 0)
                        {
                            this._data = this._data.filter(l_shopClinic => l_shopClinic.city?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.clinicOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.entOfficeId?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase())
                                || l_shopClinic.name?.toLowerCase().startsWith(this.clinicOfficeListService.filterByIdCityOrName.value.toLowerCase()))

                        }
                        this.pageIndex = 0;
                        this.loadClinicsOfficesPage()

                    })
                    this.subscription.add(l_sub)
                }
            }

        }

        let l_sub1 = this.clinicOfficeListService.filterByIdCityOrName.pipe(skip(1)).subscribe(l_input =>
        {
            this._data = this.clinicOfficeListService.data;
            if (l_input.length > 0)
            {
                this._data = this._data.filter(l_shopClinic => l_shopClinic.city?.toLowerCase().startsWith(l_input.toLowerCase()) || l_shopClinic.clinicOfficeId?.toLowerCase().startsWith(l_input.toLowerCase()) || l_shopClinic.entOfficeId?.toLowerCase().startsWith(l_input.toLowerCase()) || l_shopClinic.name?.toLowerCase().startsWith(l_input.toLowerCase()))
            }
            this.pageIndex = 0;
            this.loadClinicsOfficesPage();
        })

        this.subscription.add(l_sub1)
    }


    loadClinicsOfficesPage()
    {
        this.clinicOfficeListService.isOpenMapDisabledSubject.next(false)
        let l_result = this._data.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize)
        this.clinicsOfficesSubject.next(l_result)

    }


    sortData()
    {
        if (this.sortDirection == SortingDirectionsEnum.none)
        {
            this._data = this._data.sort(() => Math.random() - 0.5)
        }
        else
        {
            switch (this.sortingHeader)
            {
                case SortingHeadersEnum.name:
                    {
                        this.simpleSort(SortingKeysEnum.name);
                        break;
                    }
                case SortingHeadersEnum.email:
                    {
                        this.emailSort();
                        break;
                    }
                case SortingHeadersEnum.clinicOfficeId:
                    {
                        this.simpleSort(SortingKeysEnum.clinicOfficeId);
                        break;
                    }
                case SortingHeadersEnum.city:
                    {
                        this.simpleSort(SortingKeysEnum.city);
                        break;
                    }
                case SortingHeadersEnum.brand:
                    {
                        this.simpleSort(SortingKeysEnum.brand);
                        break;
                    }
                case SortingHeadersEnum.patientsAmount:
                    {
                        this.simpleSort(SortingKeysEnum.patientsAmount, true);
                        break;
                    }
                case SortingHeadersEnum.hcpAmount:
                    {
                        this.simpleSort(SortingKeysEnum.hcpAmount, true);
                        break;
                    }
                case SortingHeadersEnum.entAmount:
                    {
                        this.simpleSort(SortingKeysEnum.entAmount, true);
                        break;
                    }
                case SortingHeadersEnum.status:
                    {
                        this.boolSort(SortingKeysEnum.shown);
                        break;
                    }


            }
        }
    }



    simpleSort(key: string, isNumber: boolean = false)
    {
        let l_returnTrue = 1
        let l_returnFalse = -1

        if (this.sortDirection == SortingDirectionsEnum.desc)
        {
            l_returnTrue = -1
            l_returnFalse = 1
        }

        this._data.sort((obj1, obj2) =>
        {
            if (Object.keys(obj1).includes(key))
            {
                if (!(obj1 as any)[key] || (obj1 as any)[key] == "–")
                {
                    return 1
                }

                if (!(obj2 as any)[key] || (obj2 as any)[key] == "–")
                {
                    return -1
                }
                if ((obj1 as any)[key] > (obj2 as any)[key])
                {
                    return l_returnTrue;
                }

                if ((obj1 as any)[key] < (obj2 as any)[key])
                {
                    return l_returnFalse;
                }
            } else
            {
                throw new Error(`Key ${ key } does not found in keys of object of type ClinicOfficeListItem`)
            }
            return 0;
        });
    }

    boolSort(key: string)
    {
        let l_returnTrue = -1;
        let l_returnFalse = 1;

        if (this.sortDirection === SortingDirectionsEnum.desc)
        {
            l_returnTrue = 1;
            l_returnFalse = -1;
        }

        this._data.sort((obj1, obj2) =>
        {
            const val1 = (obj1 as any)[key];
            const val2 = (obj2 as any)[key];

            const isNullOrDash = (v: any) => v === null || v === undefined || v === "–";

            // Put nulls always at the bottom
            if (isNullOrDash(val1) && !isNullOrDash(val2)) return 1;
            if (!isNullOrDash(val1) && isNullOrDash(val2)) return -1;
            if (isNullOrDash(val1) && isNullOrDash(val2)) return 0;

            // Handle boolean
            if (typeof val1 === "boolean" && typeof val2 === "boolean")
            {
                if (val1 === val2) return 0;
                if (val1 && !val2) return l_returnTrue; // true > false
                if (!val1 && val2) return l_returnFalse;
            }

            // Fallback to string or number
            if (val1 > val2) return l_returnTrue;
            if (val1 < val2) return l_returnFalse;

            return 0;
        });
    }

    emailSort()
    {
        let l_returnTrue = 1
        let l_returnFalse = -1
        if (this.sortDirection == SortingDirectionsEnum.desc)
        {
            l_returnTrue = -1
            l_returnFalse = 1
        }

        this._data.sort((obj1, obj2) =>
        {

            if (!obj1.email || obj1.email == "–")
            {
                return 1
            }

            if (!obj2.email || obj2.email == "–")
            {
                return -1
            }

            let l_emailObj1 = obj1.email.split('@')[0]
            let l_emailObj2 = obj2.email.split('@')[0]
            if (l_emailObj1.toLowerCase() > l_emailObj2.toLowerCase())
            {
                return l_returnTrue;
            }

            if (l_emailObj1.toLowerCase() < l_emailObj2.toLowerCase())
            {
                return l_returnFalse;
            }

            return 0;
        });
    }

    filterData()
    {
        this._data = this.clinicOfficeListService.data;
    }
}
